import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const AVAILABLE_PERMISSIONS = [
  { id: 'view_dashboard', label: 'View Dashboard' },
  { id: 'manage_users', label: 'Manage Users' },
  { id: 'send_invitations', label: 'Send Invitations' },
  { id: 'manage_customers', label: 'Manage Customers' },
  { id: 'manage_projects', label: 'Manage Projects' },
  { id: 'manage_bids', label: 'Manage Bids' },
  { id: 'manage_resources', label: 'Manage Resources' },
  { id: 'view_analytics', label: 'View Analytics' },
  { id: 'configure_settings', label: 'Configure Settings' },
];

const RoleManagementTab = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    roleName: '',
    description: '',
    permissions: []
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const roles = [
    {
      name: 'Global Administrator',
      value: 'global_administrator',
      icon: 'ShieldAlert',
      color: 'text-rose-600',
      bgColor: 'bg-rose-100',
      description: 'Highest-level access with all administrator permissions',
      permissions: [
        'View and manage all users',
        'Send and manage invitations',
        'Modify user roles and permissions',
        'Access all dashboard features',
        'Manage customers and projects',
        'Create and edit bids',
        'Manage project phases and resources',
        'View analytics and reports',
        'Configure system settings'
      ]
    },
    {
      name: 'Administrator',
      value: 'administrator',
      icon: 'ShieldCheck',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      description: 'Full system access with all permissions',
      permissions: [
        'View and manage all users',
        'Send and manage invitations',
        'Modify user roles and permissions',
        'Access all dashboard features',
        'Manage customers and projects',
        'Create and edit bids',
        'Manage project phases and resources',
        'View analytics and reports',
        'Configure system settings'
      ]
    },
    {
      name: 'User',
      value: 'user',
      icon: 'User',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      description: 'Standard access for team members',
      permissions: [
        'View dashboard',
        'View and manage customers',
        'View and manage bids',
        'View and manage projects',
        'Manage project phases',
        'Manage resources',
        'View analytics',
        'Update own profile'
      ]
    },
    {
      name: 'View Only',
      value: 'view_only',
      icon: 'Eye',
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      description: 'Read-only access to view information',
      permissions: [
        'View dashboard',
        'View customers',
        'View bids',
        'View projects',
        'View own profile'
      ]
    }
  ];

  const validateField = (field, value) => {
    switch (field) {
      case 'roleName':
        if (!value?.trim()) return 'Role name is required';
        if (value?.trim()?.length < 3) return 'Role name must be at least 3 characters';
        if (value?.trim()?.length > 50) return 'Role name must be 50 characters or fewer';
        if (['administrator', 'global administrator', 'user', 'view only']?.includes(value?.trim()?.toLowerCase()))
          return 'This role name is already in use — choose a unique name';
        return '';
      case 'description':
        if (!value?.trim()) return 'A short description is required';
        if (value?.trim()?.length < 10) return 'Description must be at least 10 characters';
        return '';
      case 'permissions':
        if (!value || value?.length === 0) return 'Select at least one permission for this role';
        return '';
      default:
        return '';
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, formData?.[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (touched?.[field]) {
      const error = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const togglePermission = (permId) => {
    const updated = formData?.permissions?.includes(permId)
      ? formData?.permissions?.filter(p => p !== permId)
      : [...formData?.permissions, permId];
    handleChange('permissions', updated);
    if (touched?.permissions) {
      const error = validateField('permissions', updated);
      setErrors(prev => ({ ...prev, permissions: error }));
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const allTouched = { roleName: true, description: true, permissions: true };
    setTouched(allTouched);
    const newErrors = {};
    ['roleName', 'description', 'permissions']?.forEach(field => {
      const error = validateField(field, formData?.[field]);
      if (error) newErrors[field] = error;
    });
    setErrors(newErrors);
    if (Object.keys(newErrors)?.length > 0) return;
    setSubmitted(true);
    setFormData({ roleName: '', description: '', permissions: [] });
    setTouched({});
    setErrors({});
    setTimeout(() => setSubmitted(false), 3000);
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setFormData({ roleName: '', description: '', permissions: [] });
    setErrors({});
    setTouched({});
    setSubmitted(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-blue-900">Role Hierarchy</h3>
            <p className="text-sm text-blue-700 mt-1">
              Roles are hierarchical: Administrator &gt; User &gt; View Only. Higher roles inherit all permissions from lower roles.
            </p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {roles?.map((role) => (
          <div key={role?.value} className="bg-card border border-border rounded-lg p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-lg ${role?.bgColor} flex items-center justify-center`}>
                <Icon name={role?.icon} size={24} className={role?.color} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">{role?.name}</h3>
                <p className="text-xs text-muted-foreground">{role?.value}</p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">{role?.description}</p>

            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">Permissions</h4>
              <ul className="space-y-2">
                {role?.permissions?.map((permission, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Icon name="Check" size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground">{permission}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="text-amber-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-amber-900">Important Notes</h3>
            <ul className="text-sm text-amber-700 mt-2 space-y-1 list-disc list-inside">
              <li>Only administrators can invite new users and manage roles</li>
              <li>Users cannot modify their own role</li>
              <li>Deactivated users cannot access the system until reactivated</li>
              <li>Role changes take effect immediately upon next login</li>
            </ul>
          </div>
        </div>
      </div>
      {/* Create Custom Role Section */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
              <Icon name="PlusCircle" size={18} className="text-accent" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">Create Custom Role</h3>
              <p className="text-xs text-muted-foreground">Define a new role with specific permissions</p>
            </div>
          </div>
          {!showCreateForm && (
            <Button
              variant="outline"
              size="sm"
              iconName="Plus"
              iconPosition="left"
              onClick={() => setShowCreateForm(true)}
            >
              New Role
            </Button>
          )}
        </div>

        {showCreateForm && (
          <form onSubmit={handleSubmit} className="p-6 space-y-5" noValidate>
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Info" size={14} className="text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Fields marked with <span className="text-destructive font-medium">*</span> are required</p>
            </div>

            {/* Role Name */}
            <div className="space-y-1.5">
              <Input
                label="Role Name"
                type="text"
                placeholder="e.g. Project Manager, Finance Reviewer, Site Supervisor"
                value={formData?.roleName}
                onChange={(e) => handleChange('roleName', e?.target?.value)}
                onBlur={() => handleBlur('roleName')}
                error={touched?.roleName ? errors?.roleName : ''}
                description={!touched?.roleName || !errors?.roleName ? 'Choose a clear, descriptive name (3–50 characters)' : undefined}
                required
              />
              {touched?.roleName && !errors?.roleName && formData?.roleName && (
                <div className="flex items-center gap-1.5">
                  <Icon name="CheckCircle" size={13} className="text-green-600" />
                  <span className="text-xs text-green-600">Role name looks good</span>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <Input
                label="Description"
                type="text"
                placeholder="e.g. Manages project timelines and coordinates with field teams"
                value={formData?.description}
                onChange={(e) => handleChange('description', e?.target?.value)}
                onBlur={() => handleBlur('description')}
                error={touched?.description ? errors?.description : ''}
                description={!touched?.description || !errors?.description ? 'Briefly describe what this role is responsible for (min. 10 characters)' : undefined}
                required
              />
            </div>

            {/* Permissions */}
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <label className={`text-sm font-medium leading-none ${
                  touched?.permissions && errors?.permissions ? 'text-destructive' : 'text-foreground'
                }`}>
                  Permissions
                </label>
                <span className="text-destructive ml-1 text-sm font-medium">*</span>
              </div>
              <p className="text-xs text-muted-foreground">Select all permissions this role should have access to</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
                {AVAILABLE_PERMISSIONS?.map(perm => {
                  const isChecked = formData?.permissions?.includes(perm?.id);
                  return (
                    <label
                      key={perm?.id}
                      className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                        isChecked
                          ? 'bg-accent/10 border-accent/40 text-foreground'
                          : 'bg-background border-border text-muted-foreground hover:bg-muted/40'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-input text-accent focus:ring-ring"
                        checked={isChecked}
                        onChange={() => togglePermission(perm?.id)}
                        onBlur={() => handleBlur('permissions')}
                      />
                      <span className="text-sm font-medium">{perm?.label}</span>
                    </label>
                  );
                })}
              </div>
              {touched?.permissions && errors?.permissions && (
                <div className="flex items-center gap-1.5 mt-1">
                  <Icon name="AlertCircle" size={14} className="text-destructive" />
                  <p className="text-sm text-destructive">{errors?.permissions}</p>
                </div>
              )}
              {touched?.permissions && !errors?.permissions && formData?.permissions?.length > 0 && (
                <div className="flex items-center gap-1.5 mt-1">
                  <Icon name="CheckCircle" size={13} className="text-green-600" />
                  <span className="text-xs text-green-600">{formData?.permissions?.length} permission{formData?.permissions?.length !== 1 ? 's' : ''} selected</span>
                </div>
              )}
            </div>

            {submitted && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                <Icon name="CheckCircle" size={16} className="text-green-600" />
                <p className="text-sm text-green-700 font-medium">Custom role created successfully!</p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                iconName="PlusCircle"
                iconPosition="left"
                className="flex-1"
              >
                Create Role
              </Button>
            </div>
          </form>
        )}

        {!showCreateForm && (
          <div className="px-6 py-5 text-center">
            <p className="text-sm text-muted-foreground">No custom roles created yet. Click <strong>New Role</strong> to define a custom role with specific permissions.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleManagementTab;